/**
 * User Utility Functions
 * Centralized logic for user role checks and permissions
 */

/**
 * Check if user is exempt from payment requirements
 * Beta testers get free access to all services for testing purposes
 * 
 * Note: Admin users are intentionally NOT included here as they should
 * use the admin panel, not the user-facing services
 * 
 * @param {Object} user - User object with role property
 * @returns {boolean} - True if user is exempt from payments
 */
export const isPaymentExempt = (user) => {
  if (!user || !user.role) return false;
  
  // Only beta_tester gets payment exemption for service testing
  return user.role === 'beta_tester';
};

/**
 * Check if user has an active subscription
 * @param {Object} user - User object
 * @returns {boolean} - True if user has active subscription
 */
export const hasActiveSubscription = (user) => {
  if (!user) return false;
  
  const activeStatuses = ['monthly', 'annual', 'active'];
  return activeStatuses.includes(user.subscription_status);
};

/**
 * Check if user has payment method on file
 * @param {Object} user - User object
 * @returns {boolean} - True if payment method exists
 */
export const hasPaymentMethod = (user) => {
  if (!user) return false;
  return !!(user.payment_method || user.stripe_customer_id);
};

/**
 * Determine if user needs to add payment method before accessing a paid service
 * @param {Object} user - User object
 * @returns {boolean} - True if payment validation modal should be shown
 */
export const needsPaymentValidation = (user) => {
  if (!user) return true;
  
  // Payment exempt users (beta_tester) never need payment validation
  if (isPaymentExempt(user)) return false;
  
  // Subscribers with active subscription don't need payment validation
  if (hasActiveSubscription(user)) return false;
  
  // Free users always need to add payment method
  if (user.role === 'free_user') return true;
  
  // A la carte users need payment method on file
  if (user.role === 'a_la_carte' && !hasPaymentMethod(user)) return true;
  
  return false;
};

/**
 * Get user-friendly display name for user role
 * @param {string} role - User role
 * @returns {string} - Display name
 */
export const getRoleDisplayName = (role) => {
  const roleNames = {
    'free_user': 'Free User',
    'a_la_carte': 'A la Carte',
    'subscriber': 'Subscriber',
    'beta_tester': 'Beta Tester',
    'admin': 'Admin',
    'student': 'Student'
  };
  
  return roleNames[role] || role;
};
